import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoadingOverlay from '../common/LoadingOverlay';
import AuthCard from '../common/AuthCard/AuthCard';
import {
  Field,
  FieldLabel,
  Form,
  PasswordWrap,
  StatusMessage,
  SubmitButton,
  TextInput,
  TextLink,
} from '../common/AuthCard/AuthCard.styled';
import { useAuth } from '../../context/AuthContext';
import { getLoginReturnPathFromLocation } from '../../utils/authSessionExpired';
import { isLaunchMode } from '../../utils/launchMode';

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const returnTo = getLoginReturnPathFromLocation(location);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        navigate(returnTo, { replace: true });
      } else {
        setError(
          result.error || 'The email or password you entered is incorrect. Please try again.'
        );
      }
    } catch (error) {
      console.error('Error during sign in:', error);
      setError('Something went wrong... Please check back later.');
    } finally {
      setIsLoading(false);
    }
  };

  const footer = (
    <>
      {isLaunchMode() ? (
        <p>
          New provider?{' '}
          <TextLink to="/provider-signup" state={{ from: returnTo }}>
            Enroll your business
          </TextLink>
        </p>
      ) : (
        <p>
          Don&apos;t have an account?{' '}
          <TextLink to="/sign-up" state={{ from: returnTo }}>
            Sign up
          </TextLink>
        </p>
      )}
      <p>
        <TextLink to="/forgot-password">Forgot password?</TextLink>
      </p>
    </>
  );

  return (
    <>
      <AuthCard
        title="Sign in"
        lead="Welcome back to NKY Connect."
        footer={footer}
      >
        <Form onSubmit={handleSubmit}>
          <Field>
            <FieldLabel htmlFor="sign-in-email">Email</FieldLabel>
            <TextInput
              id="sign-in-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="sign-in-password">Password</FieldLabel>
            <PasswordWrap>
              <TextInput
                id="sign-in-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <IconButton
                size="small"
                onClick={() => setShowPassword((show) => !show)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </PasswordWrap>
          </Field>
          {error && (
            <StatusMessage role="alert" aria-live="assertive">
              {error}
            </StatusMessage>
          )}
          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? 'Signing in…' : 'Sign in'}
          </SubmitButton>
        </Form>
      </AuthCard>
      {isLoading && <LoadingOverlay />}
    </>
  );
};

export default SignIn;
